import { TabsPanel } from '#~/common/tabs-panel/index.js';
import { logger } from '#~/logger/index.js';
import { post } from '#~/notifications/utils.js';
import { PostHogFeatureFlag, useFeatureFlag } from '#~/posthog/index.js';
import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import type { DeviceEgressSelectorType } from '#~/statequery/selectors/device-egress.js';
import { useEgressErrorStore } from '#~/store/index.js';
import { motion } from '@studio/design-system/motion';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import { StatusEgressState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { setNDIDeviceStatus } from '../commands/index.js';
import { codecBlockedNoticeId, getCodecWarning } from '../components/codec-warning.js';
import { SourceCard } from '../components/source-card.js';
import { RTMP_V2 } from '../rtmp/RTMP_V2.js';
import { DestinationsProvider } from '../rtmp/store/destinations.js';
import { NDI } from './ndi.js';
import {
  displayBitrateFromBytes,
  displayResolution,
  displaySizeFromBytes,
  isOutputting as isOutputtingFn,
  useOutputting
} from './use-outputting.js';

/**
 * Renders a source card for a given device (audio or video).
 *
 * @param props - The component props.
 * @param props.source - The device egress selector data for the source.
 * @param props.selected - A record indicating which devices are currently selected.
 * @param props.onSelection - Callback function to handle device selection.
 * @param props.type - The type of the source, either 'audio' or 'video'.
 * @returns A React element representing the source card, or null if the source is undefined.
 */
// Returns the backend error string for NDI egress failures when available,
// falling back to generic messages based on state.
const getNDIErrorMessage = (state: T_Maybe<StatusEgressState>, error?: string | null) => {
  // Prefer specific backend error when available (e.g., codec validation).
  if (
    error !== undefined &&
    error !== null &&
    (state === StatusEgressState.EGRESS_STATE_FAILED ||
      state === StatusEgressState.EGRESS_STATE_FAILED_TO_START)
  ) {
    return error;
  }
  if (state === StatusEgressState.EGRESS_STATE_FAILED) {
    return 'NDI output unexpectedly stopped.';
  }
  if (state === StatusEgressState.EGRESS_STATE_FAILED_TO_START) {
    return 'NDI output could not be started.';
  }
  return undefined;
};

export const Source = ({
  source,
  selected,
  onSelection,
  type
}: {
  source: DeviceEgressSelectorType;
  selected: Record<string, boolean>;
  onSelection: (deviceId: Device['id']) => void;
  type: 'audio' | 'video';
}) => {
  const deviceSelected = selected[source?.id] ?? false;

  const selectionCallback = useCallback(() => {
    onSelection(source.id);
  }, [source.id]);

  const config = useMemo(() => getOutputConfig(source), [source]);
  const { dismiss, isDismissed } = useEgressErrorStore();

  if (source === undefined) {
    return null;
  }

  const codecWarning = ndiDeniedReason(source);
  const reactiveError = getNDIErrorMessage(
    source.egress?.ndiOutputStatus?.state,
    source.egress?.ndiOutputStatus?.error
  );
  const visibleError = isDismissed(source.id, 'ndi', reactiveError) ? undefined : reactiveError;

  return (
    <SourceCard
      stagId={source.id}
      name={source.name ?? 'Unknown'}
      isActive={isOutputtingFn(source.egress)}
      selected={deviceSelected}
      config={config}
      type={type}
      unselectableReason={codecWarning}
      onBlockedSelection={() => {
        post({
          // Pinned id: the notice renders it as its STag uniqueID, so a HIL
          // spec can address THIS toast rather than search the stack by copy.
          id: codecBlockedNoticeId('ndi', source.id),
          title: 'NDI Output unavailable for this source',
          message: `${source.name}: ${codecWarning}`,
          severity: 'warning'
        });
      }}
      error={visibleError}
      onDismissError={visibleError ? () => dismiss(source.id, 'ndi', visibleError) : undefined}
      onSelection={selectionCallback}
    />
  );
};

/**
 * Generates the output configuration for a given device.
 *
 * @param source - The device egress selector data for the source.
 * @returns An array of objects containing the name and value of the output configuration.
 */
export const getOutputConfig = (
  source: DeviceEgressSelectorType
): { name: string; duration?: T_Maybe<number>; value?: T_Maybe<React.ReactNode> }[] => {
  //const { bitrate, duration, framerate, width, height, size } = source.recording.config;

  const {
    bps: bitrate,
    duration,
    framerate,
    width,
    height,
    size
  } = source?.egress?.ndiOutputStatus ?? {};

  const sizeInBytes = (size ?? 0) * 1024;

  const isVideo = source.type === 'video';

  return [
    {
      name: 'Duration',
      duration
    },
    {
      name: 'Memory',
      value: displaySizeFromBytes(sizeInBytes ?? 0)
    },
    ...(isVideo
      ? [
          {
            name: 'Resolution',
            value: displayResolution(width ?? 0, height ?? 0)
          }
        ]
      : []),
    {
      name: 'Bitrate',
      value: displayBitrateFromBytes(bitrate ?? 0)
    },
    ...(isVideo
      ? [
          {
            name: 'FPS',
            value: Math.round(framerate ?? 0)
          }
        ]
      : [])
  ];
};

/**
 * Renders a list of sources, either outputting or non-outputting.
 *
 * @param props - The component props.
 * @param props.sources - The list of sources to render.
 * @param props.selected - A record indicating which devices are currently selected.
 */
export const SourcesList = ({
  sources,
  selected,
  onSelection,
  type
}: {
  sources: DeviceEgressSelectorType[];
  selected: Record<string, boolean>;
  onSelection: (deviceId: string) => void;
  type: 'audio' | 'video';
}) => {
  const outputtingSources = sources.filter((source) => isOutputtingFn(source.egress));
  const nonOutputtingSources = sources.filter((source) => !isOutputtingFn(source.egress));

  return (
    <>
      {[
        ...outputtingSources.map((source) => {
          return (
            <motion.div layout='position' key={source.id} layoutId={`device-${source.id}`}>
              <Source source={source} type={type} selected={selected} onSelection={onSelection} />
            </motion.div>
          );
        }),
        outputtingSources.length > 0 && (
          <motion.div layout='position' layoutId='device-divider' key='divider' className='h-20' />
        ),
        ...nonOutputtingSources.map((source) => {
          return (
            <motion.div layout='position' key={source.id} layoutId={`device-${source.id}`}>
              <Source
                type={type}
                source={source}
                key={source.id}
                selected={selected}
                onSelection={onSelection}
              />
            </motion.div>
          );
        })
      ]}
    </>
  );
};

/** NDI deny reason, or undefined when the source is missing, audio, or allowed. */
export const ndiDeniedReason = (device: T_Maybe<DeviceEgressSelectorType>): string | undefined =>
  device?.type === 'video' ? getCodecWarning('ndi', device.videoCodec) : undefined;

export const isNdiDenied = (device: T_Maybe<DeviceEgressSelectorType>): boolean =>
  Boolean(ndiDeniedReason(device));

/** True when NDI can carry this source, so the tray camera may be pre-ticked. */
export const isNdiAutoSelectable = (device: T_Maybe<DeviceEgressSelectorType>): boolean =>
  device != null && !isNdiDenied(device);

/**
 * Apply tray auto-select once the device is in the list. `consumed` is false
 * while the device is still missing so the caller can wait. Unknown/`''`
 * codecs stay selectable (absent-data-gates-permissively). Sets `true`
 * rather than toggling, so an already-outputting tray camera is not unticked.
 */
export const applyNdiTrayAutoSelect = (
  selectedVideoDevice: T_Maybe<string>,
  device: T_Maybe<DeviceEgressSelectorType>,
  prev: Record<string, boolean>
): { selected: Record<string, boolean>; consumed: boolean } => {
  if (!selectedVideoDevice || device == null) return { selected: prev, consumed: false };
  if (!isNdiAutoSelectable(device) || prev[selectedVideoDevice] === true) {
    return { selected: prev, consumed: true };
  }
  return { selected: { ...prev, [selectedVideoDevice]: true }, consumed: true };
};

/**
 * True when a picker delta on a still-present device would reach
 * setNDIDeviceStatus. Walks `selected` only (same as main) — a key that
 * exists only in `current` is not a user change. Denied-codec selects and
 * ids gone from `devices` are ignored; deselect still counts.
 */
export const ndiHasStartableChanges = (
  devices: DeviceEgressSelectorType[],
  selected: Record<string, boolean>,
  current: Record<string, boolean>
): boolean => {
  const byId = Object.fromEntries(devices.map((device) => [device.id, device]));
  for (const key of Object.keys(selected)) {
    if ((selected[key] ?? false) === (current[key] ?? false)) continue;
    const device = byId[key];
    if (device === undefined) continue;
    if ((selected[key] ?? false) && isNdiDenied(device)) continue;
    return true;
  }
  return false;
};

/** True when Save would enable at least one source that commit will actually start. */
export const ndiWouldStartAny = (
  devices: DeviceEgressSelectorType[],
  selected: Record<string, boolean>
): boolean => {
  const byId = Object.fromEntries(devices.map((device) => [device.id, device]));
  return Object.keys(selected).some((key) => {
    if (!selected[key]) return false;
    const device = byId[key];
    return device !== undefined && !isNdiDenied(device);
  });
};

/** What NDI is doing right now per device, from broker state. */
export const ndiCurrentSelection = (devices: DeviceEgressSelectorType[]): Record<string, boolean> =>
  Object.fromEntries(devices.map((device) => [device.id, isOutputtingFn(device.egress)]));

/**
 * Applies the NDI picker selection. Denied codecs never start. Only deltas
 * against `current` are sent: every Save used to push `enable: false` to each
 * untouched device too, which queued a Stop Egress job per device on the
 * encoder and re-sent starts the encoder rejects as "already started". Each
 * mutation is awaited so a rejection is surfaced instead of becoming an
 * unhandled promise rejection in the room (QA 2026-09-14, mixed H.264/H.265
 * Studio: NDI failed and every source card vanished).
 */
export const commitNdiOutput = async (
  devices: DeviceEgressSelectorType[],
  selected: Record<string, boolean>,
  current: Record<string, boolean>
): Promise<void> => {
  const { clear } = useEgressErrorStore.getState();
  const sent: DeviceEgressSelectorType[] = [];
  const sends: Promise<void>[] = [];
  for (const device of devices) {
    const deviceId = device.id;
    const deviceType = device.type;
    const wantEnable = selected[deviceId] ?? false;
    if (wantEnable === (current[deviceId] ?? false)) {
      continue;
    }
    if (wantEnable && isNdiDenied(device)) {
      continue;
    }
    if (wantEnable) {
      clear(deviceId, 'ndi');
    }
    sent.push(device);
    sends.push(
      setNDIDeviceStatus(deviceId, {
        enable: wantEnable,
        video: deviceType === 'video',
        audio: deviceType === 'audio'
      })
    );
  }
  const results = await Promise.allSettled(sends);
  results.forEach((result, index) => {
    if (result.status !== 'rejected') return;
    const device = sent[index];
    if (device === undefined) return;
    // Per feedback: use loggy, not console (memory) — product code logs via logger.
    logger.warning('[ndi-output] NDI change was not applied', {
      deviceId: device.id,
      enable: selected[device.id] ?? false,
      cause: result.reason
    });
    post({
      title: 'NDI Output',
      message: `${device.name}: the NDI change could not be applied.`,
      severity: 'error'
    });
  });
};

/** `<Output>` component. */
export const Output = ({
  defaultOutputTab,
  agentId,
  selectedVideoDevice
}: {
  defaultOutputTab?: 'ndi-output' | undefined;
  agentId: T_Maybe<Agent['id']>;
  selectedVideoDevice: T_Maybe<Device['id']>;
}): ReactElement => {
  const { stopOutput, devices } = useOutputting();

  const [unsafeIsStarting, setUnsafeIsStarting] = useState(false);
  const [unsafeIsStopping, setUnsafeIsStopping] = useState(false);

  const { enabled: isRTMPEnabled } = useFeatureFlag(PostHogFeatureFlag.RTMP_OUTPUT_FEATURE, {
    defaultValue: false
  });

  const { enabled: isNDIEnabled } = useFeatureFlag(PostHogFeatureFlag.NDI_OUTPUT_FEATURE, {
    defaultValue: false
  });

  // 1) Grab devices from state, and map them to if they are outputting or not
  //    Be mindful of the amount of devices, hence, the for `of` loop
  const deviceSelectionStatus: Record<string, boolean> = {};
  for (const [id, device] of Object.entries(devices)) {
    deviceSelectionStatus[id] = isOutputtingFn(device.egress);
  }
  // 2) Set the initial selected state to the device selection status
  const [selected, setSelected] = useState<Record<string, boolean>>(deviceSelectionStatus);

  // Wait until the tray camera is in `devices` (broker state can arrive after
  // mount). Unknown/`''` codecs still tick — this does not close the stale
  // unknown→H265 path. Denied codecs are consumed without ticking.
  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (didAutoSelect.current || !selectedVideoDevice) return;
    const device = devices[selectedVideoDevice];
    if (device === undefined) return;
    didAutoSelect.current = true;
    setSelected((prev) => applyNdiTrayAutoSelect(selectedVideoDevice, device, prev).selected);
  }, [selectedVideoDevice, devices]);

  const { videoSources, audioSources } = useMemo(() => {
    const sources = Object.values(devices).filter((d) => !d.id.startsWith('tmp'));
    return {
      videoSources: sources.filter((d) => d.type === 'video'),
      audioSources: sources.filter((d) => d.type === 'audio')
    };
  }, [devices]);

  const onSave = useCallback(() => {
    const all = Object.values(devices);
    // commitNdiOutput reports its own rejections; nothing to await here.
    void commitNdiOutput(all, selected, ndiCurrentSelection(all));

    const starting = ndiWouldStartAny(Object.values(devices), selected);
    if (starting) {
      setUnsafeIsStarting(true);
    } else {
      setUnsafeIsStopping(true);
    }

    setTimeout(() => {
      setUnsafeIsStarting(false);
      setUnsafeIsStopping(false);
    }, 10_000);
  }, [selected, devices]);

  const onSafeStop = useCallback(() => {
    setUnsafeIsStopping(true);
    stopOutput();
    setTimeout(() => {
      setUnsafeIsStopping(false);
    }, 10_000);
  }, [stopOutput]);

  const hasChanges = useMemo(
    () => ndiHasStartableChanges(Object.values(devices), selected, deviceSelectionStatus),
    [deviceSelectionStatus, devices, selected]
  );

  const onSelection = useCallback((deviceId: string) => {
    setSelected((prev) => ({ ...prev, [deviceId]: !prev[deviceId] }));
  }, []);

  const isOutputting = Object.values(devices).some(
    (device) => device.egress?.ndiOutputStatus?.state === StatusEgressState.EGRESS_STATE_ACTIVE
  );
  const isStarting = !isOutputting && unsafeIsStarting;
  const isStopping = isOutputting && unsafeIsStopping;

  const tabContent = [];

  // Only add RTMP tab if enabled
  if (isRTMPEnabled) {
    tabContent.push({
      label: 'RTMP',
      value: 'rtmp-v2-output',
      // Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — the
      // HIL specs pick this sub-tab by tag, not by getByRole('tab', ...).
      triggerStag: STagNew(Sel.ProductionOutput, 'tab', 'rtmp'),
      content: (
        <div className='contents' {...DataSTag(Sel.ProductionOutput, 'capability', 'rtmp')}>
          <RTMP_V2
            agentId={agentId}
            selectedVideoDevice={selectedVideoDevice}
            videoSources={videoSources}
            hasChanges={hasChanges}
          />
        </div>
      )
    });
  }

  // Only add NDI tab if enabled
  if (isNDIEnabled) {
    tabContent.push({
      label: 'NDI',
      value: 'ndi-output',
      triggerStag: STagNew(Sel.ProductionOutput, 'tab', 'ndi'),
      content: (
        <div className='contents' {...DataSTag(Sel.ProductionOutput, 'capability', 'ndi')}>
          <NDI
            isStopping={isStopping}
            isStarting={isStarting}
            isOutputting={isOutputting}
            hasChanges={hasChanges}
            onSafeStop={onSafeStop}
            onSave={onSave}
            videoSources={videoSources}
            audioSources={audioSources}
            selected={selected}
            onSelection={onSelection}
          />
        </div>
      )
    });
  }

  const determineDefaultValue = useCallback(() => {
    // N9 opens the failed NDI output even when RTMP is also enabled.
    if (defaultOutputTab === 'ndi-output' && isNDIEnabled) return 'ndi-output';
    // Prefer RTMP if both are enabled, otherwise use whichever is enabled
    if (isRTMPEnabled) return 'rtmp-v2-output';
    if (isNDIEnabled) return 'ndi-output';
    return 'ndi-output'; // Fallback
  }, [isRTMPEnabled, isNDIEnabled, defaultOutputTab]);

  // Add safety check: if no output features enabled, show message
  if (!isRTMPEnabled && !isNDIEnabled) {
    return (
      <div className='grow px-40 py-20'>
        <div className='text-center text-muted'>
          <p>No output features are currently enabled.</p>
          <p className='mt-8 text-sm'>Contact your administrator to enable output features.</p>
        </div>
      </div>
    );
  }

  return (
    <DestinationsProvider>
      <div className='grow px-40'>
        <TabsPanel
          defaultValue={determineDefaultValue()}
          triggerClassName='h-auto'
          listClassName='mb-12'
          applySubcategoryTriggerClassName={true}
          tabs={tabContent}
        />
      </div>
    </DestinationsProvider>
  );
};
